import { useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Patient, Encounter, MedicationStatement } from '@medplum/fhirtypes';
import { prepareMedicationForSave } from '@/pages/ainigma/patient/examination/examination.helpers';
import {
  setStatus,
  setEffectivePeriod,
  setStatusReason,
  parseEffectiveStart,
} from '@/utils/medication.utils';

export interface UseMedicationActionsReturn {
  /**
   * Save a medication to the server (create or update).
   * Returns the saved medication with server-assigned ID.
   */
  saveMedication: (
    medication: MedicationStatement
  ) => Promise<MedicationStatement>;

  /**
   * Stop a medication with an end date and optional reason.
   * Saves immediately to server.
   */
  stopMedication: (
    medication: MedicationStatement,
    endDate: Date,
    reason?: string
  ) => Promise<MedicationStatement>;

  /**
   * Soft delete a medication (mark as entered-in-error).
   * This preserves the audit trail while removing from active view.
   */
  deleteMedication: (id: string) => Promise<void>;
}

/**
 * Hook for managing medication server operations.
 * Centralizes all FHIR MedicationStatement create/update/delete operations.
 *
 * @param patient - The patient resource
 * @param encounter - The current encounter resource
 * @returns Object with saveMedication, stopMedication, and deleteMedication functions
 *
 * @example
 * ```tsx
 * const { saveMedication, stopMedication, deleteMedication } = useMedicationActions(patient, encounter);
 *
 * // Save a new medication
 * const saved = await saveMedication(newMedication);
 *
 * // Stop an existing medication
 * const stopped = await stopMedication(medication, new Date(), 'Side effects');
 *
 * // Delete (soft) a medication
 * await deleteMedication(medicationId);
 * ```
 */
export function useMedicationActions(
  patient: Patient,
  encounter: Encounter
): UseMedicationActionsReturn {
  const medplum = useMedplum();

  const saveMedication = useCallback(
    async (medication: MedicationStatement): Promise<MedicationStatement> => {
      // Prepare the FHIR resource for saving
      const resource = prepareMedicationForSave(
        patient,
        encounter,
        medication
      );

      // Create or update based on whether we have an ID
      const saved = medication.id
        ? await medplum.updateResource(resource)
        : await medplum.createResource(resource);

      // Invalidate cache so subsequent reads get fresh data
      medplum.invalidateSearches('MedicationStatement');

      return saved;
    },
    [medplum, patient, encounter]
  );

  const stopMedication = useCallback(
    async (
      medication: MedicationStatement,
      endDate: Date,
      reason?: string
    ): Promise<MedicationStatement> => {
      // Build stopped medication using utility functions
      let stoppedMed = setStatus(medication, 'stopped');
      stoppedMed = setEffectivePeriod(stoppedMed, parseEffectiveStart(medication), endDate);
      if (reason) {
        stoppedMed = setStatusReason(stoppedMed, reason);
      }

      // Use saveMedication to persist
      return saveMedication(stoppedMed);
    },
    [saveMedication]
  );

  const deleteMedication = useCallback(
    async (id: string): Promise<void> => {
      // Soft delete - mark as entered-in-error
      const existing = await medplum.readResource('MedicationStatement', id);
      await medplum.updateResource({
        ...existing,
        status: 'entered-in-error',
      });

      // Invalidate cache
      medplum.invalidateSearches('MedicationStatement');
    },
    [medplum]
  );

  return { saveMedication, stopMedication, deleteMedication };
}
